<template>
  <div id="workbench" :class="{
    'is-collapsed': isCollapsed,
    'layout-defaults': currentLayout === 'defaults',
    'layout-transverse': currentLayout === 'transverse',
  }">
    <!-- defaults 布局：左侧边栏 + 顶部header + 主内容 -->
    <template v-if="currentLayout === 'defaults'">
      <el-container>
        <!-- 左侧导航栏 -->
        <el-aside :width="sidebarWidth" class="sidebar">
          <!-- Logo -->
          <div v-if="isShowLogo" class="layout-logo" :class="{ 'logo-collapsed': isCollapsed }" @click="toggleSidebar">
            <span class="logo-icon">🐋</span>
            <span class="logo-text">快打分销</span>
          </div>
          <el-menu :default-active="activeIndex" :size="componentSize" :collapse="isCollapsed"
            :collapse-transition="false" class="sidebar-menu" router :background-color="menuBarBgColor"
            :text-color="menuBarTextColor" :active-text-color="primaryColor" :unique-opened="isUniqueOpened">
            <!-- 侧边栏导航 -->
            <el-menu-item :index="item.path" v-for="item in menuRoutes" :key="item.path">
              <i :class="item.icon"></i>
              <span slot="title">{{ item.title }}</span>
            </el-menu-item>
          </el-menu>
        </el-aside>

        <el-container>
          <!-- 顶部导航栏 -->
          <el-header class="top-header layout-navbars-breadcrumb-index">
            <!-- 面包屑（包含折叠按钮） -->
            <div v-if="isBreadcrumb" class="header-breadcrumb layout-navbars-breadcrumb">
              <!-- 折叠/展开按钮 -->
              <div class="collapse-btn-header layout-navbars-breadcrumb-icon" @click="toggleSidebar" title="折叠/展开侧边栏">
                <i :class="isCollapsed
                  ? 'el-icon-d-arrow-right'
                  : 'el-icon-d-arrow-left'
                  "></i>
              </div>
              <el-breadcrumb separator="/">
                <el-breadcrumb-item v-for="(item, index) in breadcrumbs" class="layout-navbars-breadcrumb-hide"
                  :key="index">
                  <span class="layout-navbars-breadcrumb-span">
                    <i v-if="isBreadcrumbIcon && item.icon"
                      :class="['layout-navbars-breadcrumb-iconfont', item.icon]"></i>
                    <span :style="[
                      index === 0
                        ? {
                          fontWeight: '900',
                          fontSize: '15px',
                          color: '#000',
                        }
                        : null,
                    ]">{{ item.title }}</span>
                  </span>
                </el-breadcrumb-item>
              </el-breadcrumb>
            </div>
            <!-- 当面包屑隐藏时，显示独立的折叠按钮 -->
            <div v-else class="collapse-btn-header-standalone">
              <div class="collapse-btn-header layout-navbars-breadcrumb-icon" @click="toggleSidebar" title="折叠/展开侧边栏">
                <i :class="isCollapsed
                  ? 'el-icon-d-arrow-right'
                  : 'el-icon-d-arrow-left'
                  "></i>
              </div>
            </div>

            <div class="header-right">
              <el-dropdown trigger="click" @command="handleSizeChange">
                <el-tooltip content="切换组件大小" placement="bottom" effect="light">
                  <div class="header-icon-item svg-icon-wrapper">
                    <svg class="header-svg-icon" viewBox="0 0 1024 1024" version="1.1"
                      xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                      <path
                        d="M748.307692 196.923077a19.692308 19.692308 0 0 1 3.544616 39.069538L748.307692 236.307692h-256v590.769231a19.692308 19.692308 0 0 1-39.069538 3.544615L452.923077 827.076923V236.307692H196.923077a19.692308 19.692308 0 0 1-3.544615-39.069538L196.923077 196.923077h551.384615z m-196.923077 315.076923a19.692308 19.692308 0 0 1-3.544615-39.069538L551.384615 472.615385h315.076923a19.692308 19.692308 0 0 1 3.544616 39.069538L866.461538 512h-137.846153v315.076923a19.692308 19.692308 0 0 1-39.069539 3.544615L689.230769 827.076923v-315.076923H551.384615z"
                        fill="currentColor"></path>
                    </svg>
                  </div>
                </el-tooltip>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="medium" :class="{ 'is-active': componentSize === 'medium' }">
                    <span>中等 (Medium)</span>
                    <i v-if="componentSize === 'medium'" class="el-icon-check"></i>
                  </el-dropdown-item>
                  <el-dropdown-item command="small" :class="{ 'is-active': componentSize === 'small' }">
                    <span>较小 (Small)</span>
                    <i v-if="componentSize === 'small'" class="el-icon-check"></i>
                  </el-dropdown-item>
                  <el-dropdown-item command="mini" :class="{ 'is-active': componentSize === 'mini' }">
                    <span>迷你 (Mini)</span>
                    <i v-if="componentSize === 'mini'" class="el-icon-check"></i>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-tooltip content="布局配置" placement="bottom" effect="light">
                <div class="header-icon-item svg-icon-wrapper" @click="openLayoutSettings">
                  <svg class="header-svg-icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    width="18" height="18">
                    <path
                      d="M637.951 262.077c-23.961 37.949-71.625 62.257-124.388 62.257-53.048 0-100.942-24.572-124.775-62.874L225 319.614v152.03l72.396-25.705c19.528-6.933 40.038 7.549 40.038 28.271v295.14h349.448V473.968c0-20.722 20.51-35.204 40.037-28.27L800 471.643v-152.03l-162.049-57.537zM205.038 542.402C185.51 549.335 165 534.853 165 514.13v-215.7a30 30 0 0 1 19.962-28.271l206.814-73.43a30 30 0 0 1 10.037-1.73h3.5c13.625 0 25.54 9.183 29.01 22.359 6.815 25.875 39.346 46.975 79.24 46.975s72.425-21.1 79.24-46.975C596.275 204.183 608.19 195 621.815 195c4.793 0 8.187 0.585 11.41 1.73l206.814 73.43A30 30 0 0 1 860 298.43v215.7c0 20.723-20.51 35.205-40.038 28.272l-73.08-25.948V799.35c0 16.568-13.432 30-30 30H307.434c-16.568 0-30-13.432-30-30V516.697l-72.396 25.705z"
                      fill="currentColor"></path>
                  </svg>
                </div>
              </el-tooltip>
              <el-tooltip content="消息通知" placement="bottom" effect="light">
                <el-badge :value="unreadCount" :hidden="unreadCount === 0"
                  class="badge-notification">
                  <i class="el-icon-bell header-icon-item" style="height: 25px;width: 25px;"
                    @click="openNotificationDrawer"></i>
                </el-badge>
              </el-tooltip>
              <el-dropdown trigger="hover" @command="handleUserCommand">
                <el-tooltip content="用户菜单" placement="bottom" effect="light">
                  <span class="user-dropdown">
                    <i class="el-icon-user"></i>
                    <span class="username">{{ nickName }}</span>
                    <i class="el-icon-arrow-down"></i>
                  </span>
                </el-tooltip>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="settings">
                    <i class="el-icon-setting"></i>
                    <span style="margin-left: 5px">个人设置</span>
                  </el-dropdown-item>
                  <el-dropdown-item command="logout" class="logout-item" title="退出登录">
                    <i class="el-icon-switch-button"></i>
                    <span style="margin-left: 5px">退出登录</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </el-header>

          <!-- 主内容区 -->
          <el-main class="main-content">
            <!-- 动态导航标签（TagsView） -->
            <div v-if="isTagsview && showNavTabs && navTabs.length > 0" class="waiBu">
              <div class="nav-tabs-container" ref="tagsViewContainer">
                <div v-for="tab in navTabs" :key="tab.path" :data-path="tab.path" :class="[
                  'nav-tab-item',
                  { 'nav-tab-active': isActiveRoute(tab.path) },
                ]" @click="switchTab(tab.path)">
                  <i v-if="isTagsviewIcon" :class="tab.icon"></i>
                  <span>{{ tab.title }}</span>
                  <i v-if="isActiveRoute(tab.path)" class="el-icon-refresh refresh-icon" @click.stop="handleRefresh">
                  </i>
                  <i class="el-icon-close close-icon" @click.stop="closeTab(tab.path)"
                    v-if="navTabs.length > 1 && isAllowCloseTagsView">
                  </i>
                </div>
              </div>
              <div class="waiLeft">
                <SyncOrderButton />
              </div>
            </div>

            <div class="main-router" ref="mainRouter" :style="routerStyle">
              <transition name="slide" mode="out-in" @enter="onSlideEnter" @after-leave="onSlideAfterLeave">
                <keep-alive>
                  <router-view></router-view>
                </keep-alive>
              </transition>
            </div>

            <div v-if="isFooter" class="main-footer">
              Copyright © 2021-present 快打分销 All rights reserved.
            </div>
          </el-main>
        </el-container>
      </el-container>
    </template>

    <!-- transverse 布局：顶部header（包含横向菜单）+ 主内容 -->
    <template v-else-if="currentLayout === 'transverse'">
      <el-container class="is-vertical">

        <!-- 顶部导航栏（包含横向菜单） -->
        <el-header class="top-header layout-navbars-breadcrumb-index transverse-header">
          <div class="transverse-menu-container">
            <div v-if="isShowLogo" class="transverse-logo">
              <span class="logo-icon">🐋</span>
              <span class="logo-text">快打分销</span>
            </div>
            <el-menu :default-active="activeIndex" :size="componentSize" mode="horizontal" class="transverse-menu"
              router :background-color="menuBarBgColor" :text-color="menuBarTextColor"
              :active-text-color="primaryColor">
              <!-- 顶部横向菜单（支持扁平化结构） -->
              <el-submenu v-for="item in menuRoutesWithChildren" :key="item.path" :index="item.path">
                <template slot="title">
                  <i :class="item.icon"></i>
                  <span slot="title">{{ item.title }}</span>
                </template>
                <el-menu-item :index="value.path" v-for="value in item.children" :key="value.path">
                  <i :class="value.icon"></i>
                  <span slot="title">{{ value.title }}</span>
                </el-menu-item>
              </el-submenu>
              <!-- 扁平化菜单直接显示 -->
              <el-menu-item v-for="item in menuRoutesFlat" :key="item.path" :index="item.path">
                <i :class="item.icon"></i>
                <span slot="title">{{ item.title }}</span>
              </el-menu-item>
            </el-menu>
          </div>
          <div class="header-right">
            <el-dropdown trigger="click" @command="handleSizeChange">
              <el-tooltip content="切换组件大小" placement="bottom" effect="light">
                <div class="header-icon-item svg-icon-wrapper">
                  <svg class="header-svg-icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    width="20" height="20">
                    <path
                      d="M748.307692 196.923077a19.692308 19.692308 0 0 1 3.544616 39.069538L748.307692 236.307692h-256v590.769231a19.692308 19.692308 0 0 1-39.069538 3.544615L452.923077 827.076923V236.307692H196.923077a19.692308 19.692308 0 0 1-3.544615-39.069538L196.923077 196.923077h551.384615z m-196.923077 315.076923a19.692308 19.692308 0 0 1-3.544615-39.069538L551.384615 472.615385h315.076923a19.692308 19.692308 0 0 1 3.544616 39.069538L866.461538 512h-137.846153v315.076923a19.692308 19.692308 0 0 1-39.069539 3.544615L689.230769 827.076923v-315.076923H551.384615z"
                      fill="currentColor"></path>
                  </svg>
                </div>
              </el-tooltip>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="medium" :class="{ 'is-active': componentSize === 'medium' }">
                  <span>中等 (Medium)</span>
                  <i v-if="componentSize === 'medium'" class="el-icon-check"></i>
                </el-dropdown-item>
                <el-dropdown-item command="small" :class="{ 'is-active': componentSize === 'small' }">
                  <span>较小 (Small)</span>
                  <i v-if="componentSize === 'small'" class="el-icon-check"></i>
                </el-dropdown-item>
                <el-dropdown-item command="mini" :class="{ 'is-active': componentSize === 'mini' }">
                  <span>迷你 (Mini)</span>
                  <i v-if="componentSize === 'mini'" class="el-icon-check"></i>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-tooltip content="布局配置" placement="bottom" effect="light">
              <div class="header-icon-item svg-icon-wrapper" @click="openLayoutSettings">
                <svg class="header-svg-icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg"
                  width="18" height="18">
                  <path
                    d="M637.951 262.077c-23.961 37.949-71.625 62.257-124.388 62.257-53.048 0-100.942-24.572-124.775-62.874L225 319.614v152.03l72.396-25.705c19.528-6.933 40.038 7.549 40.038 28.271v295.14h349.448V473.968c0-20.722 20.51-35.204 40.037-28.27L800 471.643v-152.03l-162.049-57.537zM205.038 542.402C185.51 549.335 165 534.853 165 514.13v-215.7a30 30 0 0 1 19.962-28.271l206.814-73.43a30 30 0 0 1 10.037-1.73h3.5c13.625 0 25.54 9.183 29.01 22.359 6.815 25.875 39.346 46.975 79.24 46.975s72.425-21.1 79.24-46.975C596.275 204.183 608.19 195 621.815 195c4.793 0 8.187 0.585 11.41 1.73l206.814 73.43A30 30 0 0 1 860 298.43v215.7c0 20.723-20.51 35.205-40.038 28.272l-73.08-25.948V799.35c0 16.568-13.432 30-30 30H307.434c-16.568 0-30-13.432-30-30V516.697l-72.396 25.705z"
                    fill="currentColor"></path>
                </svg>
              </div>
            </el-tooltip>
            <el-tooltip content="消息通知" placement="bottom" effect="light">
            <el-badge :value="unreadCount" :hidden="unreadCount === 0"
              class="badge-notification">
              <i class="el-icon-bell header-icon-item" style="height: 25px;width: 25px;"
                @click="openNotificationDrawer"></i>
            </el-badge>
          </el-tooltip>
            <el-dropdown trigger="hover" @command="handleUserCommand">
              <el-tooltip content="用户菜单" placement="bottom" effect="light">

                <span class="user-dropdown">
                  <i class="el-icon-user"></i>
                  <span class="username">{{ nickName }}</span>
                  <i class="el-icon-arrow-down"></i>
                </span>
              </el-tooltip>


              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="settings">
                  <i class="el-icon-setting"></i>
                  <span style="margin-left: 5px">个人设置</span>
                </el-dropdown-item>
                <el-dropdown-item command="logout" class="logout-item" title="退出登录">
                  <i class="el-icon-switch-button"></i>
                  <span style="margin-left: 5px">退出登录</span>
                </el-dropdown-item>
              </el-dropdown-menu>


            </el-dropdown>
          </div>
        </el-header>
        <!-- 主内容区 -->
        <el-container>
          <el-container class="is-vertical">
            <el-main class="main-content">
              <div v-if="isTagsview && showNavTabs && navTabs.length > 0" class="waiBu">
                <div class="nav-tabs-container" ref="tagsViewContainer">
                  <div v-for="tab in navTabs" :key="tab.path" :data-path="tab.path" :class="[
                    'nav-tab-item',
                    { 'nav-tab-active': isActiveRoute(tab.path) },
                  ]" @click="switchTab(tab.path)">
                    <i v-if="isTagsviewIcon" :class="tab.icon"></i>
                    <span>{{ tab.title }}</span>
                    <i v-if="isActiveRoute(tab.path)" class="el-icon-refresh refresh-icon"
                      @click.stop="handleRefresh"></i>
                    <i class="el-icon-close close-icon" @click.stop="closeTab(tab.path)" v-if="navTabs.length > 1"></i>
                  </div>
                </div>
                <div class="waiLeft">
                  <SyncOrderButton />
                </div>
              </div>

              <div class="main-router" ref="mainRouter" :style="routerStyle">
                <transition name="slide" mode="out-in" @enter="onSlideEnter" @after-leave="onSlideAfterLeave">
                  <keep-alive>
                    <router-view></router-view>
                  </keep-alive>
                </transition>
              </div>
              <div v-if="isFooter" class="main-footer">
                Copyright © 2021-present 快打分销 All rights reserved.
              </div>
            </el-main>
          </el-container>
        </el-container>
      </el-container>
    </template>

    <!-- 客服悬浮按钮 -->
    <div class="customer-service-float" @click.stop="openCustom" ref="customerServiceFloat"
      @mouseenter="handleServiceMouseEnter" @mouseleave="handleServiceMouseLeave">
      <el-tooltip content="双击联系客服" placement="left" effect="dark">
        <div class="service-button" @mousedown="startDrag" :class="{ 'hovered': isServiceHovered }">
          客服
        </div>
      </el-tooltip>
      <!-- <div class="service-panel" v-show="isServiceHovered">
        <div class="service-header">
          <h3>客服中心</h3>
        </div>
        <div class="service-content">
          <p class="service-time">客服在线时间：9:00 - 20:00</p>
          <div class="service-qrcode">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=客服微信" alt="客服二维码">
          </div>
          <p class="service-contact">客服联系方式：</p>
          <p class="service-wechat">微信：customer_service</p>
        </div>
      </div> -->
    </div>

    <!-- 消息通知抽屉 -->
    <el-drawer title="消息通知" :visible.sync="notificationDrawerVisible" :direction="'rtl'" size="360px"
      @close="closeNotificationDrawer">
      <div class="notification-container">
        <div v-if="notificationList.length === 0" class="empty-notification">
          <i class="el-icon-bell" style="font-size: 48px; color: #ccc;"></i>
          <p>暂无消息通知</p>
        </div>
        <div v-else class="notification-list">
          <div v-for="(item, index) in notificationList" :key="item.id || index" class="notification-item">
            <div class="notification-header">
              <span class="notification-title">{{ item.title }}</span>
              <span :class="['notification-level', item.level.toLowerCase()]">{{ item.level }}</span>
            </div>
            <div class="notification-content">{{ item.content }}</div>
            <div class="notification-footer">
              <span class="notification-time">{{ item.occurredAt }}</span>
              <span v-if="item.shopNumber" class="notification-shop">店铺: {{ item.shopNumber }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
// api获取
import { Local, Session } from "@/utils/storage";
import { useChangeColor } from "@/utils/theme";
import SyncOrderButton from "@/components/SyncOrderButton";
import { getInfo } from "@/api/login";
import { list } from "@/api/option";
import { listOne, pushMessage } from "@/api/system";

//ShopNotifyRead标记已读
import { ShopNotifyList, ShopNotifyRead, ShopNotifyReadAll } from "@/api/notify";
  

// 动态导入sortablejs，避免未安装时报错
let Sortable = null;
try {
  // 使用 require 导入 sortablejs
  const sortableModule = require("sortablejs");
  // sortablejs 可能导出为 default 或者直接导出构造函数
  // 处理不同的导出方式
  if (sortableModule.default) {
    Sortable = sortableModule.default;
  } else if (typeof sortableModule === "function") {
    Sortable = sortableModule;
  } else if (sortableModule.Sortable) {
    Sortable = sortableModule.Sortable;
    
  } else {
    Sortable = sortableModule;
  }
} catch (e) {
  // 如果 require 失败，尝试使用全局变量
  if (typeof window !== "undefined" && window.Sortable) {
    Sortable = window.Sortable;
  } else {
    console.warn(
      "sortablejs未安装或导入失败，TagsView拖拽功能将不可用。请运行: npm install sortablejs"
    );
  }
}

// 路由配置映射已改为从 menuRoutes 动态生成（见 computed.routeConfig）

export default {
  name: "workbench",
  components: { SyncOrderButton },
  provide() {
    return {
      componentSize: this.componentSize,
    };
  },
  data() {
    return {
      nickName: "",
      navTabs: [], // 动态标签列表
      componentSize: "medium", // 组件大小：medium / small / mini
      isCollapsed: false, // 侧边栏是否折叠
      cachedViews: [], // 缓存的视图名称列表（需要与组件的 name 属性匹配）
      sortableInstance: null, // sortable实例
      themeConfig: null, // 响应式的主题配置对象
      addTabTimer: null, // 添加标签的防抖定时器
      routerStyle: {}, // 路由容器的样式，用于保持高度
      isProgrammaticNavigation: false, // 标志位：是否程序化路由切换（避免重复添加标签）

      // 客服悬浮按钮相关
      isServiceHovered: false, // 客服按钮是否被 hover
      isDragging: false, // 是否正在拖拽
      dragStartX: 0, // 拖拽开始的 X 坐标
      dragStartY: 0, // 拖拽开始的 Y 坐标
      floatButtonPosition: { // 悬浮按钮的位置
        right: '20px',
        bottom: '20px'
      },
      // 点击次数和定时器，用于实现双击跳转
      // 消息通知抽屉相关
      notificationDrawerVisible: false,
      notificationList: [

      ],
      unreadCount: 0,
      clientId: '1d82681b010beca374099f4dfb7cd526',
      sseConnection: null,
      sseReconnectTimer: null,
      sseConnected: false,
      sseReconnectCount: 0,
      maxReconnectAttempts: 5,

      menuRoutes: [
        // {
        //   title: "工作台",
        //   icon: "el-icon-s-home",
        //   path: "/workbench/dashboard",
        // },
        {
          title: "交易订单",
          icon: "el-icon-sell",
          path: "/workbench/salesOrder",
        },
        {
          title: "店铺管理",
          icon: "el-icon-shopping-bag-2",
          path: "/workbench/purchaseShopList",
        },
        {
          title: "采购账号",
          icon: "el-icon-s-shop",
          path: "/workbench/TaobaoShop",
        },
        // {
        //   title: "采购订单",
        //   icon: "el-icon-shopping-bag-1",
        //   path: "/workbench/purchaseOrder",
        // },
        {
          title: "地址管理",
          icon: "el-icon-location",
          path: "/workbench/ReturnAddress",
        }, 
        // {
        //   title: "退货地址配置",
        //   icon: "el-icon-refresh-left",
        //   path: "/workbench/UniversalAddress",
        // },
        // {
        //   title: "商品列表",
        //   icon: "el-icon-shopping-cart-2",
        //   path: "/workbench/purchase",
        // },
        {
          title: "利润管理",
          icon: "el-icon-money",
          path: "/workbench/DataProfit",
        },
        {
          title: "账号管理",
          icon: "el-icon-user",
          path: "/workbench/accountManagement",
        },
      ],
    };
  },
  computed: {
    sidebarWidth() {
      return this.isCollapsed ? "70px" : "185px";
    },
    activeIndex() {
      const path = this.$route.path;
      if (path.startsWith("/workbench")) {
        return path;
      }
      return path;
    },
    showNavTabs() {
      return this.$route.path.startsWith("/workbench");
    },
    breadcrumbs() {
      const crumbs = [];
      const currentPath = this.$route.path;
      const { parent, child } = this.findMenuRoute(currentPath);
      if (parent) {
        crumbs.push({
          title: parent.title,
          icon: parent.icon,
        });
      }
      if (child && child !== parent) {
        crumbs.push({
          title: child.title,
          icon: child.icon,
        });
      }
      return crumbs;
    },
    menuBarBgColor() {
      // 优先使用响应式配置，确保主题变化时能及时更新
      const config = this.themeConfig || Local.get("themeConfig");
      return (config && config.menuBar) || "#fff";
    },
    menuBarTextColor() {
      // 优先使用响应式配置，确保主题变化时能及时更新                                                                                                                                                        为谔谔谔谔谔谔谔谔谔谔谔谔谔谔谔谔谔谔 
      const config = this.themeConfig || Local.get("themeConfig");
      return (config && config.menuBarColor) || "#333";
    },
    primaryColor() {
      // 优先使用响应式配置，确保主题变化时能及时更新
      const config = this.themeConfig || Local.get("themeConfig");
      return (config && config.primary) || "#0f59a4";
    },
    // 激活菜单项的背景色（主题色的透明版本）
    activeMenuBgColor() {
      const { hexToRgb } = useChangeColor();
      const primary = this.primaryColor;
      try {
        const rgb = hexToRgb(primary);
        if (rgb && rgb.length === 3) {
          return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.1)`;
        }
      } catch (e) {
        console.warn("生成激活菜单背景色失败", e);
      }
      return "rgba(15, 89, 164, 0.1)"; // 默认值
    },
    isShowLogo() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isShowLogo !== false : true;
    },
    isBreadcrumb() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isBreadcrumb !== false : true;
    },
    isBreadcrumbIcon() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isBreadcrumbIcon !== false : true;
    },

    isTagsview() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isTagsview !== false : true;
    },

    isTagsviewIcon() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isTagsviewIcon !== false : true;
    },

    isFooter() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isFooter !== false : true;
    },
    isUniqueOpened() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isUniqueOpened !== false : true;
    },
    isCacheTagsView() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isCacheTagsView !== false : true;
    },
    isSortableTagsView() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isSortableTagsView !== false : true;
    },
    isShareTagsView() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isShareTagsView === true : false;
    },
    isAllowCloseTagsView() {
      const config = this.themeConfig || Local.get("themeConfig");
      return config ? config.isAllowCloseTagsView !== false : true;
    },
    currentLayout() {
      const config = this.themeConfig || Local.get("themeConfig");
      return (config && config.layout) || "defaults";
    },
    // 从 menuRoutes 动态生成路由配置映射

    routeConfig() {
      const config = {};
      // 遍历 menuRoutes 数组（扁平化结构）
      this.menuRoutes.forEach((item) => {
        // 如果有子路由，遍历子路由（兼容嵌套结构）
        if (item.children && Array.isArray(item.children)) {
          item.children.forEach((child) => {
            config[child.path] = {
              title: child.title,
              icon: child.icon,
              path: child.path,
            };
          });
        }

        // 如果当前项有 path（扁平化结构的直接菜单项），添加到配置中
        if (item.path) {
          config[item.path] = {
            title: item.title,
            icon: item.icon,
            path: item.path,
          };
        }
      });
      return config;
    },
    // 分离有子菜单的路由项
    menuRoutesWithChildren() {
      return this.menuRoutes.filter(item => item.children && item.children.length > 0);
    },
    // 分离扁平化的路由项
    menuRoutesFlat() {
      return this.menuRoutes.filter(item => !item.children || item.children.length === 0);
    },
  },
  watch: {
    // 动态添加标签在这里
    $route(to) {
      // 如果是程序化路由切换（如关闭标签、切换标签），检查标签是否存在，不存在则添加
      if (this.isProgrammaticNavigation) {
        this.isProgrammaticNavigation = false;
        if (
          to.path.startsWith("/workbench") &&
          !this.navTabs.some((tab) => tab.path === to.path)
        ) {
          this.addTab(to.path);
        }
        this.updateCachedViews();
        return;
      }

      if (to.path.startsWith("/workbench")) {
        if (this.addTabTimer) {
          clearTimeout(this.addTabTimer);
        }
        this.addTabTimer = setTimeout(() => {
          this.addTab(to.path);
          this.updateCachedViews();
          this.addTabTimer = null;
        }, 100);
      }
    },
    themeConfig: {
      handler(newVal, oldVal) {
        // 只在主题色相关配置变化时更新菜单样式
        if (newVal && oldVal) {
          const colorChanged =
            newVal.primary !== oldVal.primary ||
            newVal.menuBar !== oldVal.menuBar ||
            newVal.menuBarColor !== oldVal.menuBarColor;

          if (colorChanged) {
            // 延迟更新，避免频繁更新导致闪烁
            this.$nextTick(() => {
              this.updateActiveMenuStyle();
            });
          }
        } else {
          // 初始加载时也要更新
          this.$nextTick(() => {
            this.updateActiveMenuStyle();
          });
        }
        // 更新拖拽功能
        this.$nextTick(() => {
          this.initSortable();
        });
      },
      deep: true,
    },
    isSortableTagsView(newVal) {
      // 监听拖拽配置变化
      this.$nextTick(() => {
        this.initSortable();
      });
    },
    navTabs: {
      handler() {
        // 保存标签列表到sessionStorage
        if (this.isCacheTagsView) {
          Session.set("tagsViewList", this.navTabs);
        }
        // 更新缓存视图
        this.updateCachedViews();
      },
      deep: true,
    },
  },

  mounted() {
    // 初始化响应式配置对象
    this.updateThemeConfig();

    // 从 localStorage 读取组件大小设置
    const savedSize = localStorage.getItem("componentSize");
    if (savedSize && ["medium", "small", "mini"].includes(savedSize)) {
      this.componentSize = savedSize;
      this.updateGlobalSize(savedSize);
    }

    // 从 localStorage 读取侧边栏折叠状态
    const savedCollapse = localStorage.getItem("sidebarCollapsed");
    if (savedCollapse === "true") {
      this.isCollapsed = true;
    }

    // 监听localStorage变化（跨标签页同步）
    window.addEventListener("storage", this.handleStorageChange);

    // 定时检查配置变化（同标签页内）
    this.configCheckInterval = setInterval(() => {
      this.updateThemeConfig();
    }, 100);

    // 从sessionStorage恢复标签列表（如果开启了缓存）
    if (this.isCacheTagsView) {
      const savedTabs = Session.get("tagsViewList");
      if (savedTabs && savedTabs.length > 0) {
        this.navTabs = savedTabs;
        // 确保当前路由的标签存在
        const currentPath = this.$route.path;
        if (
          currentPath.startsWith("/workbench") ||
          currentPath.startsWith("/purchase") ||
          currentPath.startsWith("/shop") ||
          currentPath.startsWith("/AddressConfiguration") ||
          currentPath.startsWith("/useInfo") ||
          currentPath.startsWith("/Profit")
        ) {
          const exists = this.navTabs.some((tab) => tab.path === currentPath);

          if (!exists) {
            this.addTab(currentPath);
          }

        }
      } else {
        // 初始化：添加默认的第一个路由标签


        // const defaultPath = "/workbench/dashboard";
        // this.addTab(defaultPath);


      }
    } else {
      // 初始化：添加默认的第一个路由标签
      let defaultPath = "/workbench/purchaseOrder";
      if (this.$route.path.startsWith("/workbench")) {
        // defaultPath = "/workbench/dashboard"; // 暂时注释
        defaultPath = "/workbench/purchaseOrder";
      } else if (this.$route.path.startsWith("/purchase")) {
        defaultPath = "/workbench/purchaseOrder";
      } else if (this.$route.path.startsWith("/shop")) {
        defaultPath = "/workbench/purchaseShopList";
      } else if (this.$route.path.startsWith("/AddressConfiguration")) {
        defaultPath = "/workbench/ReturnAddress";
      } else if (this.$route.path.startsWith("/useInfo")) {
        defaultPath = "/workbench/accountManagement";
      } else if (this.$route.path.startsWith("/Profit")) {
        defaultPath = "/workbench/DataProfit";
      }

      this.addTab(defaultPath);

      // 如果当前路由不是默认路径，且属于工作区路由，则添加标签页（不依赖routeConfig）
      if (
        (this.$route.path.startsWith("/workbench") ||
          this.$route.path.startsWith("/purchase") ||
          this.$route.path.startsWith("/shop") ||
          this.$route.path.startsWith("/AddressConfiguration") ||
          this.$route.path.startsWith("/useInfo") ||
          this.$route.path.startsWith("/Profit")) &&
        this.$route.path !== defaultPath
      ) {
        // 检查标签是否已存在
        const exists = this.navTabs.some((tab) => tab.path === this.$route.path);
        if (!exists) {
          // 尝试从routeConfig获取标题，如果没有则使用路径
          const config = this.routeConfig[this.$route.path];
          const tabTitle = config ? config.title : this.$route.path;
          // 直接添加标签页，确保页面能正常渲染
          this.navTabs.push({
            title: tabTitle,
            path: this.$route.path,
            name: this.$route.name || ''
          });
        }
      }
    }




    // 初始化拖拽功能
    this.$nextTick(() => {
      this.initSortable();
    });

    // 加载并应用主题配置
    this.loadThemeConfig();

    // 监听主题配置变化事件
    this.$root.$on("theme-config-changed", () => {
      // 更新响应式配置
      this.updateThemeConfig();
      // 延迟更新激活菜单样式，确保配置已更新
      this.$nextTick(() => {
        this.updateActiveMenuStyle();
      });
    });
    // 监听Logo变化
    this.$root.$on("logo-change", (show) => {
      this.updateThemeConfig();
      this.$forceUpdate();
    });
    // 监听Breadcrumb变化
    this.$root.$on("breadcrumb-change", (show) => {
      this.updateThemeConfig();
      this.$forceUpdate();
    });
    // 监听TagsView拖拽变化
    this.$root.$on("openOrCloseSortable", () => {
      this.updateThemeConfig();
      this.$nextTick(() => {
        this.initSortable();
      });
    });
    // 监听TagsView共用变化
    this.$root.$on("openShareTagsView", () => {
      this.updateThemeConfig();
      // 如果开启共用模式，需要清理重复的标签
      if (this.isShareTagsView) {
        const uniqueTabs = [];
        const pathSet = new Set();
        this.navTabs.forEach((tab) => {
          if (!pathSet.has(tab.path)) {
            pathSet.add(tab.path);
            uniqueTabs.push(tab);
          }
        });
        this.navTabs = uniqueTabs;
      }
      this.$forceUpdate();
    });
    // 监听布局变化
    this.$root.$on("layout-change", (layout) => {
      this.updateThemeConfig();
      // 布局切换后，强制更新视图并重新渲染路由
      this.$nextTick(() => {
        // 清除路由样式，确保重新计算
        this.routerStyle = {};
        this.$forceUpdate();
        // 重新初始化拖拽功能
        this.initSortable();
        // 更新激活菜单样式
        this.updateActiveMenuStyle();
        // 确保 router-view 重新渲染
        this.$nextTick(() => {
          if (this.$refs.mainRouter) {
            // 确保容器可见

            const routerEl = this.$refs.mainRouter;
            if (routerEl) {


              routerEl.style.display = "block";
              routerEl.style.visibility = "visible";
              routerEl.style.opacity = "1";
            }
          }
        });
      });
      this.$message.success(`已切换到${this.getLayoutName(layout)}布局`);
    });
    // 监听侧边栏折叠变化
    this.$root.$on("sidebar-collapse-change", (collapsed) => {
      this.isCollapsed = collapsed;
      localStorage.setItem("sidebarCollapsed", collapsed.toString());
    });
  },
  beforeDestroy() {
    // 移除事件监听
    this.$root.$off("theme-config-changed");
    this.$root.$off("logo-change");
    this.$root.$off("breadcrumb-change");
    this.$root.$off("openOrCloseSortable");
    this.$root.$off("openShareTagsView");
    this.$root.$off("layout-change");
    this.$root.$off("sidebar-collapse-change");
    // 移除storage监听
    window.removeEventListener("storage", this.handleStorageChange);
    // 清除定时器
    if (this.configCheckInterval) {
      clearInterval(this.configCheckInterval);
    }
    if (this.addTabTimer) {
      clearTimeout(this.addTabTimer);
      this.addTabTimer = null;
    }
    // 销毁sortable实例
    if (this.sortableInstance) {
      try {
        // 检查元素是否还存在
        const container = this.$refs.tagsViewContainer;
        if (container) {
          this.sortableInstance.destroy();
        }
      } catch (e) {
        // 如果销毁失败，忽略错误（可能元素已被移除）
        console.warn("销毁 sortable 实例时出错:", e);
      } finally {
        this.sortableInstance = null;
      }
    }
  },
  methods: {
    // 店铺消息列表
    getShopNotifyList() {
      ShopNotifyList({
        pageNum: this.pageNum,
        pageSize: this.pageSize,
      }).then(res => {
        if (res.data.code == 200) {
          this.shopNotifyList = res.data.data.records;
          this.total = res.data.data.total;
        }
      });
    },
    // 检查路由是否存在于权限路由中
    checkRouteExists(path) {
      // 遍历 menuRoutes 检查路由是否存在（支持扁平化结构）
      for (const menuItem of this.menuRoutes) {
        // 检查当前项是否匹配（扁平化结构）
        if (menuItem.path === path) {
          return true;
        }
        // 检查子路由（兼容嵌套结构）
        if (menuItem.children && menuItem.children.length > 0) {
          const found = menuItem.children.some(child => child.path === path);
          if (found) return true;
        }
      }
      return false;
    },

    getRouters() {
      return new Promise((resolve) => {
        listOne().then((res) => {
          if (res.data.code == 200) {

            // 处理权限路由数据
            this.handlePermissionRoutes(res.data.data);
          }
          resolve();
        }).catch(() => {
          resolve();
        });
      });
    },

    handlePermissionRoutes(permissionRoutes) {
      // 如果权限路由为空或无效，保留原始菜单
      if (!permissionRoutes || !Array.isArray(permissionRoutes) || permissionRoutes.length === 0) {
        return;
      }

      // 提取所有有权限的子路由路径
      const permissionPaths = new Set();

      // 遍历权限路由，只提取子路由的路径
      permissionRoutes.forEach(route => {
        if (route.children && route.children.length > 0) {
          route.children.forEach(child => {
            if (child.path && child.path !== '//' && child.path !== '/') {
              const cleanPath = child.path.replace(/^\/\//, '/');
              permissionPaths.add(cleanPath);
              // 同时添加不带/workbench前缀的路径（兼容旧格式）
              if (cleanPath.startsWith('/workbench/')) {
                const shortPath = cleanPath.replace('/workbench', '');
                permissionPaths.add(shortPath);
              }
            }
          });
        } else {
          // 对于没有子路由的项，检查自身是否有权限
          if (route.path && route.path !== '//' && route.path !== '/') {
            const cleanPath = route.path.replace(/^\/\//, '/');
            permissionPaths.add(cleanPath);
            // 同时添加不带/workbench前缀的路径（兼容旧格式）
            if (cleanPath.startsWith('/workbench/')) {
              const shortPath = cleanPath.replace('/workbench', '');
              permissionPaths.add(shortPath);
            }
          }
        }
      });


      // 如果没有任何权限路径，保留原始菜单
      if (permissionPaths.size === 0) {
        return;
      }

      // 过滤 menuRoutes，保留有权限的路由（支持扁平化结构）
      const finalMenuRoutes = this.menuRoutes.map(menuItem => {
        if (menuItem.children && menuItem.children.length > 0) {
          // 过滤子菜单项
          const filteredChildren = menuItem.children.filter(child => {
            const childPath = child.path.replace(/^\/\//, '/');
            return permissionPaths.has(childPath) ||
              permissionPaths.has(childPath.replace('/workbench', ''));
          });

          // 如果没有有权限的子项，返回 null（后续会过滤掉）
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...menuItem,
            children: filteredChildren
          };
        } else {
          // 对于扁平化结构的菜单项（没有子菜单），检查自身是否有权限
          const menuPath = menuItem.path.replace(/^\/\//, '/');
          const hasPermission = permissionPaths.has(menuPath) ||
            permissionPaths.has(menuPath.replace('/workbench', ''));
          return hasPermission ? menuItem : null;
        }
      }).filter(Boolean); // 过滤掉 null 项


      // 更新 menuRoutes（只有当过滤结果非空时）
      if (finalMenuRoutes.length > 0) {
        this.menuRoutes = finalMenuRoutes;
      }
    },
    openCustom() {
      // 清除之前的定时器
      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
      }

      // 增加点击次数
      this.clickCount++;

      // 如果点击次数达到2次，跳转客服
      if (this.clickCount === 2) {
        window.open("http://amos.alicdn.com/getcid.aw?v=2&uid=%E5%91%A8%E5%8F%A3%E7%AB%A3%E6%BA%90%E5%95%86%E8%B4%B8%E6%9C%89%E9%99%90%E5%85%AC%E5%8F%B8&site=cnalichn&s=1&groupid=0&charset=utf-8", "_blank");
        this.clickCount = 0; // 重置点击次数
      } else {
        // 设置定时器，300ms内没有再次点击则重置
        this.clickTimer = setTimeout(() => {
          this.clickCount = 0;
        }, 300);
      }
    },
    // 客服悬浮按钮相关方法
    // 开始拖拽
    startDrag(event) {
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      event.preventDefault();
    },
    // 拖拽中
    onDrag(event) {
      if (!this.isDragging) return;

      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;

      const floatElement = this.$refs.customerServiceFloat;
      if (floatElement) {
        const currentRight = parseFloat(floatElement.style.right) || 20;
        const currentBottom = parseFloat(floatElement.style.bottom) || 20;

        // 计算新位置，确保按钮不会超出可视区域
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const buttonWidth = 60;
        const buttonHeight = 60;

        // 计算新的右侧和底部位置
        let newRight = currentRight - dx;
        let newBottom = currentBottom - dy;

        // 限制在可视区域内
        if (newRight < 10) newRight = 10;
        if (newBottom < 10) newBottom = 10;
        if (windowWidth - newRight - buttonWidth < 10) newRight = windowWidth - buttonWidth - 10;
        if (windowHeight - newBottom - buttonHeight < 10) newBottom = windowHeight - buttonHeight - 10;

        floatElement.style.right = newRight + 'px';
        floatElement.style.bottom = newBottom + 'px';

        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
      }
    },
    // 停止拖拽
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    // 鼠标移入客服按钮
    handleServiceMouseEnter() {
      this.isServiceHovered = true;
    },
    // 鼠标移出客服区域
    handleServiceMouseLeave() {
      this.isServiceHovered = false;
    },

    // 加上一个初始化的函数，方便没有登陆 跳转到登陆页面
    init() {
      getInfo().then((res) => {
        if (res.data.code == 200) {
          this.$store.commit("setUserInfo", res.data.data);
          this.nickName = res.data.data.nickName;
        }
      });
    },

    findMenuRoute(path) {
      let parent = null;
      let child = null;
      const normalize = (p) => p.replace(/\/+$/, "");
      const isMatch = (routePath, targetPath) => {
        const normalizedRoute = normalize(routePath);
        const normalizedTarget = normalize(targetPath);
        return (
          normalizedRoute === normalizedTarget ||
          normalizedTarget.startsWith(`${normalizedRoute}/`)
        );
      };

      for (const item of this.menuRoutes) {
        // 检查子路由（兼容嵌套结构）
        if (item.children && item.children.length > 0) {
          const matchedChild = item.children.find((childItem) =>
            isMatch(childItem.path, path)
          );
          if (matchedChild) {
            parent = item;
            child = matchedChild;
            break;
          }
        }

        // 检查当前项（扁平化结构）
        if (isMatch(item.path, path)) {
          parent = item;
          child = item; // 扁平化结构中，父和子都是同一个项
          break;
        }
      }

      return { parent, child: child || parent };
    },
    // 获取布局名称
    getLayoutName(layout) {
      const layoutNames = {
        defaults: "默认",
        transverse: "横向",
      };
      return layoutNames[layout] || layout;
    },
    // 添加标签
    addTab(path) {
      let config = this.routeConfig[path];

      // 如果没有配置，使用默认配置（确保标签页能正常添加）
      if (!config) {
        config = {
          title: path,
          path: path,
          name: this.$route.name || ''
        };
      }

      // 检查是否已存在相同路径的标签
      const existingTab = this.navTabs.find((tab) => tab.path === path);

      // TagsView共用模式：相同路由只显示一个标签
      if (this.isShareTagsView) {
        if (existingTab) {
          // 如果已存在，不重复添加，也不切换（避免循环）
          return;
        } else {
          this.navTabs.push({
            ...config,
          });
        }
      } else {
        // 非共用模式：如果已存在相同路径的标签，不重复添加
        if (existingTab) {
          // 如果已存在相同路径的标签，不重复添加
          return;
        }
        // 只有在不存在相同路径的标签时，才添加新标签
        const tabId = `${path}_${Date.now()}`;
        this.navTabs.push({
          ...config,
          tabId: tabId,
          timestamp: Date.now(),
        });
      }
    },
    // 关闭标签
    closeTab(path) {
      // 在共用模式下，找到第一个匹配的标签
      // 在非共用模式下，找到所有匹配的标签（可能有多个）
      let index = -1;
      if (this.isShareTagsView) {
        index = this.navTabs.findIndex((tab) => tab.path === path);
      } else {
        // 非共用模式：找到当前激活的标签（如果有tabId则精确匹配）
        const currentTab = this.navTabs.find(
          (tab) => tab.path === path && this.isActiveRoute(tab.path)
        );
        if (currentTab && currentTab.tabId) {
          index = this.navTabs.findIndex(
            (tab) => tab.tabId === currentTab.tabId
          );
        } else {
          index = this.navTabs.findIndex((tab) => tab.path === path);
        }
      }

      if (index === -1) return;

      // 如果关闭的是当前激活的标签，需要切换到其他标签
      const isActive = this.isActiveRoute(path);
      let nextTab = null;

      if (isActive && this.navTabs.length > 1) {
        // 优先切换到右侧的标签，如果没有则切换到左侧
        nextTab = this.navTabs[index + 1] || this.navTabs[index - 1];
      }

      // 先移除标签
      this.navTabs.splice(index, 1);

      // 从缓存中移除对应的视图
      const route = this.$router.resolve(path);
      if (route && route.route && route.route.name) {
        const cachedIndex = this.cachedViews.indexOf(route.route.name);
        if (cachedIndex > -1) {
          this.cachedViews.splice(cachedIndex, 1);
        }
      }

      // 如果关闭的是当前激活的标签，切换到其他标签
      if (isActive) {
        if (nextTab) {
          // 设置标志位，避免路由切换时重复添加标签
          this.isProgrammaticNavigation = true;
          this.$router.push(nextTab.path);
        } else if (this.navTabs.length === 0) {
          this.isProgrammaticNavigation = true;
          if (this.$route.path.startsWith("/purchase")) {
            this.$router.push("/workbench/purchaseOrder");
          } else if (this.$route.path.startsWith("/shop")) {
            this.$router.push("/workbench/purchaseShopList");
          } else if (this.$route.path.startsWith("/AddressConfiguration")) {
            this.$router.push("/workbench/ReturnAddress");
          } else if (this.$route.path.startsWith("/useInfo")) {
            this.$router.push("/workbench/accountManagement");
          } else {
            this.$router.push("/workbench/purchaseOrder");
          }
        }
      }
    },
    // 切换标签
    switchTab(path) {
      // 设置标志位，避免路由切换时重复添加标签
      this.isProgrammaticNavigation = true;
      this.$router.push(path);
    },
    // 判断路由是否激活
    isActiveRoute(path) {
      return (
        this.$route.path === path ||
        // (path === "/workbench/dashboard" && this.$route.path === "/workbench") ||

        (path === "/workbench/purchaseOrder" && this.$route.path === "/workbench") ||

        (path === "/workbench/purchaseShopList" && this.$route.path === "/workbench") ||

        (path === "/workbench/ReturnAddress" && this.$route.path === "/workbench") ||

        (path === "/workbench/accountManagement" && this.$route.path === "/workbench")
      );
    },
    // 刷新
    handleRefresh() {
      // 从缓存中移除当前路由
      const routeName = this.$route.name;
      if (routeName && this.cachedViews.includes(routeName)) {
        const index = this.cachedViews.indexOf(routeName);
        this.cachedViews.splice(index, 1);
      }

      this.$nextTick(() => {
        // 重新添加到缓存
        if (routeName) {
          this.cachedViews.push(routeName);
        }
        // 强制刷新路由视图
        this.$router.replace({
          path: this.$route.path,
          query: { _t: Date.now() },
        });
      });

      this.$message({
        message: "刷新成功",
        type: "success",
      });
    },
    // 初始化拖拽功能
    initSortable() {
      const container = this.$refs.tagsViewContainer;
      if (!container) return;

      // 如果sortablejs未安装，不初始化
      if (!Sortable) {
        console.warn("sortablejs未安装，无法使用拖拽功能");
        return;
      }

      // 销毁旧实例
      if (this.sortableInstance) {
        try {
          // 检查元素是否还存在
          const oldContainer = this.$refs.tagsViewContainer;
          if (oldContainer) {
            this.sortableInstance.destroy();
          }
        } catch (e) {
          // 如果销毁失败，忽略错误（可能元素已被移除）
          console.warn("销毁旧 sortable 实例时出错:", e);
        } finally {
          this.sortableInstance = null;
        }
      }

      // 如果未开启拖拽，不初始化
      if (!this.isSortableTagsView) {
        return;
      }

      // 创建sortable实例
      // sortablejs 的标准用法是 new Sortable()
      if (typeof Sortable === "function") {
        try {
          this.sortableInstance = new Sortable(container, {
            animation: 300,
            dataIdAttr: "data-path",
            disabled: !this.isSortableTagsView,
            // 关闭 body 回退/强制 fallback，避免拖拽克隆元素残留导致 tab 背景重叠
            forceFallback: false,
            fallbackOnBody: false,
            onEnd: (evt) => {
              try {
                // 保存容器引用和当前顺序，避免在 Vue 更新 DOM 后引用失效
                const currentContainer = this.$refs.tagsViewContainer;
                if (!currentContainer) return;

                // 先获取当前顺序
                const newTabs = [];
                const children = Array.from(currentContainer.children);
                children.forEach((child) => {
                  const path = child.getAttribute("data-path");
                  const tab = this.navTabs.find((t) => t.path === path);
                  if (tab) {
                    newTabs.push(tab);
                  }
                });

                // 使用 setTimeout 延迟更新，确保 sortable 完成所有清理工作（包括 _onDrop）
                setTimeout(() => {
                  try {
                    // 再次检查容器是否存在
                    const container = this.$refs.tagsViewContainer;
                    if (container) {
                      this.navTabs = newTabs;
                    }
                  } catch (e) {
                    console.warn("更新标签顺序时出错:", e);
                  }
                }, 0);
              } catch (e) {
                console.warn("拖拽结束时出错:", e);
              }
            },
          });
        } catch (e) {
          console.error("创建 sortable 实例时出错:", e);
        }
      } else {
        console.warn(
          "Sortable 不是构造函数，无法创建拖拽实例。请检查 sortablejs 是否正确安装。"
        );
      }
    },
    // 更新响应式配置对象
    updateThemeConfig() {
      const savedConfig = Local.get("themeConfig");
      if (savedConfig) {
        // 使用Vue.set确保响应式

        this.$set(this, "themeConfig", { ...savedConfig });
      } else {
        this.$set(this, "themeConfig", null);
      }
    },
    // 处理localStorage变化事件（跨标签页）

    handleStorageChange(e) {
      if (e.key === "themeConfig" || e.key === null) {
        this.updateThemeConfig();
      }
    },


    // 更新缓存的视图列表
    updateCachedViews() {
      if (!this.isCacheTagsView) {
        this.cachedViews = [];
        return;
      }

      // 根据navTabs和路由配置，确定需要缓存的组件名称
      const views = [];
      const routeMap = {};
      this.navTabs.forEach((tab) => {
        // 使用路由配置直接获取name
        const route = this.$router.options.routes.find((r) => {
          if (r.path === tab.path) return r;
          if (r.children) {
            return r.children.find(
              (child) =>
                child.path === tab.path ||
                `${r.path}/${child.path}` === tab.path
            );
          }
          return false;
        });

        let routeName = null;
        if (route && route.name) {
          routeName = route.name;
        } else if (route && route.children) {
          const childRoute = route.children.find(
            (child) =>
              child.path === tab.path ||
              `${route.path}/${child.path}` === tab.path
          );
          if (childRoute && childRoute.name) {
            routeName = childRoute.name;
          }
        }

        // 从routeConfig获取name（如果路由配置中有）
        if (!routeName && this.routeConfig[tab.path]) {
          // 尝试从路由配置中获取
          const matchedRoute = this.$router.resolve(tab.path);

          if (matchedRoute && matchedRoute.route && matchedRoute.route.name) {
            routeName = matchedRoute.route.name;
          }




        }

        if (routeName && !views.includes(routeName)) {
          views.push(routeName);
        }
      });
      this.cachedViews = views;
    },
    // 处理组件大小改变
    handleSizeChange(size) {
      this.componentSize = size;
      localStorage.setItem("componentSize", size);
      this.updateGlobalSize(size);
      // this.$message({
      //   message: `组件大小已设置为 ${size === "medium" ? "中等" : size === "small" ? "较小" : "迷你"
      //     }`,
      //   type: "success",
      // });
      location.reload();
      this.$message({
        message: `组件大小已设置为 ${size === "medium" ? "中等" : size === "small" ? "较小" : "迷你"
          }`,
        type: "success",
      });
    },
    // 更新全局组件大小
    updateGlobalSize(size) {
      // 更新 Element UI 的全局 size 配置
      if (this.$ELEMENT) {
        this.$ELEMENT.size = size;
      }
      // 通过事件通知所有组件更新大小
      this.$root.$emit("size`-change", size);
      // 通过 key 变化强制重新渲染 router-view，使所有子组件重新创建
      // 这样所有组件都会使用新的 size 配置
    },
    // 切换侧边栏折叠状态
    toggleSidebar() {
      // 直接切换，不使用防抖，避免延迟感
      this.isCollapsed = !this.isCollapsed;
      // 异步保存到 localStorage，不阻塞渲染
      setTimeout(() => {
        localStorage.setItem("sidebarCollapsed", this.isCollapsed.toString());
      }, 0);
    },
    // 处理用户下拉菜单命令
    handleUserCommand(command) {
      if (command === "settings") {
        // 个人设置 - 跳转到店铺配置页面
        this.$router.push("/workbench/purchaseShopList");
      } else if (command === "logout") {
        // 退出登录
        this.handleLogout();
      }
    },
    // 退出登录
    handleLogout() {
      this.$confirm("确定要退出登录吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          // 关闭SSE连接
          this.closeSSEConnection();

          // 清除本地存储的登录信息
          localStorage.removeItem("access_token");
          localStorage.removeItem("scope");
          localStorage.removeItem("componentSize");
          localStorage.removeItem("sidebarCollapsed");
          localStorage.removeItem("storeList");

          // 清除导航标签
          this.navTabs = [];
          // 跳转到登录页
          this.$router.push("/login");

          this.$message({
            type: "success",
            message: "退出登录成功",
          });
        })
        .catch(() => {
          // 用户取消退出
        });
    },
    // 打开布局配置
    openLayoutSettings() {
      // 通过事件通知App.vue打开布局配置
      this.$root.$emit("openLayoutSettings");
    },
    // 加载主题配置（简化逻辑，主要依赖App.vue中恢复的cssText）
    loadThemeConfig() {
      const savedConfig = Local.get("themeConfig");
      const savedStyle = Local.get("themeConfigStyle");

      // CSS变量已经在App.vue中恢复了，这里只需要应用渐变效果
      if (savedConfig && savedStyle) {
        this.$nextTick(() => {
          setTimeout(() => {
            // 应用渐变效果
            if (savedConfig.isTopBarColorGradual && savedConfig.topBar) {
              const topHeader = document.querySelector(".top-header");
              if (topHeader) {
                const { getLightColor } = useChangeColor();
                topHeader.setAttribute(
                  "style",
                  `background: linear-gradient(to bottom, ${savedConfig.topBar
                  }, ${getLightColor(savedConfig.topBar, 0.5)})`
                );
              }
            }

            if (savedConfig.isMenuBarColorGradual && savedConfig.menuBar) {
              const sidebar = document.querySelector(".sidebar");
              if (sidebar) {
                const { getLightColor } = useChangeColor();
                sidebar.setAttribute(
                  "style",
                  `background: linear-gradient(to bottom, ${savedConfig.menuBar
                  }, ${getLightColor(savedConfig.menuBar, 0.5)})`
                );
              }
            }

            // 更新激活菜单项的样式（使用主题色）
            this.updateActiveMenuStyle();
          }, 300);
        });
      } else {
        // 即使没有保存的配置，也要更新激活菜单样式
        this.$nextTick(() => {
          this.updateActiveMenuStyle();
        });
      }
    },
    // 更新激活菜单项的样式
    updateActiveMenuStyle() {
      const { hexToRgb } = useChangeColor();
      const primary = this.primaryColor;

      try {
        const rgb = hexToRgb(primary);
        if (rgb && rgb.length === 3) {
          const bgColor = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.1)`;
          // 设置 CSS 变量
          document.documentElement.style.setProperty(
            "--el-menu-active-bg-color",
            bgColor
          );
          document.documentElement.style.setProperty(
            "--el-menu-active-text-color",
            primary
          );
        }
      } catch (e) {
        console.warn("更新激活菜单样式失败", e);
      }
    },
    // 路由切换动画 - 进入
    onSlideEnter(el) {
      // 新页面进入时，保持当前容器高度
      this.$nextTick(() => {
        if (this.$refs.mainRouter) {
          const height = this.$refs.mainRouter.scrollHeight;
          this.routerStyle = { minHeight: `${height}px` };
        }
      });
    },
    // 路由切换动画 - 离开后
    onSlideAfterLeave() {
      // 动画结束后，恢复自动高度
      this.$nextTick(() => {
        setTimeout(() => {
          this.routerStyle = {};
        }, 50);
      });
    },

    // 打开消息通知抽屉
    openNotificationDrawer() {
      // 点击进入后清零未读数量
      this.unreadCount = 0;
      this.notificationDrawerVisible = true;
    },

    // 关闭消息通知抽屉
    closeNotificationDrawer() {
      this.notificationDrawerVisible = false;
    },

    // 初始化SSE连接
    initSSEConnection() {
      if (this.sseConnected || this.sseConnection) {
        return;
      }

      const token = localStorage.getItem("access_token");
      if (!token) return;

      const baseUrl = process.env.NODE_ENV === 'development'
        ? 'http://192.168.101.72:8080'
        : 'https://1688.lanjingyuncang.cn/prod-api';

      const url = `${baseUrl}/resource/sse?clientid=${this.clientId}&Authorization=${encodeURIComponent('Bearer ' + token)}`;

      console.log("建立SSE连接:", url);
      this.sseConnection = new EventSource(url, {
        withCredentials: true
      });

      this.sseConnection.onopen = (event) => {
        console.log("SSE连接已建立", event);
        this.sseConnected = true;
        this.sseReconnectCount = 0;
        if (this.sseReconnectTimer) {
          clearTimeout(this.sseReconnectTimer);
          this.sseReconnectTimer = null;
        }
      };
      this.sseConnection.onmessage = (event) => {
        console.log("SSE消息:", event.data);
        try {
          const messageData = JSON.parse(event.data);
          console.log("解析后的消息数据:", messageData);
          // 将新消息添加到列表开头
          this.notificationList.unshift(messageData);
          // 新消息到来时增加未读数量
          this.unreadCount++;
          // 最多保留100条消息
          if (this.notificationList.length > 100) {
            this.notificationList = this.notificationList.slice(0, 100);
          }
        } catch (error) {
          console.error("消息解析失败:", error);
        }
      };

      this.sseConnection.onerror = (error) => {
        console.error("SSE连接错误:", error);
        this.sseConnected = false;
        this.closeSSEConnection();
        this.scheduleReconnect();
      };
      this.sseConnection.addEventListener('close', () => {
        console.log("SSE连接已关闭");
        this.sseConnected = false;
        this.closeSSEConnection();
      });
    },

    // 定时重连（阶梯退避策略）
    scheduleReconnect() {
      if (this.sseReconnectCount >= this.maxReconnectAttempts) {
        console.log(`已达到最大重连次数(${this.maxReconnectAttempts}次)，停止尝试`);
        return;
      }

      this.sseReconnectCount++;
      const delay = Math.pow(2, this.sseReconnectCount - 1) * 1000;

      console.log(`SSE重连尝试 ${this.sseReconnectCount}/${this.maxReconnectAttempts}，${delay / 1000}秒后重试...`);

      if (this.sseReconnectTimer) {
        clearTimeout(this.sseReconnectTimer);
      }
      this.sseReconnectTimer = setTimeout(() => {
        this.initSSEConnection();
      }, delay);
    },

    // 关闭SSE连接
    closeSSEConnection() {
      if (this.sseConnection) {
        this.sseConnection.close();
        this.sseConnection = null;
      }
      this.sseConnected = false;
      if (this.sseReconnectTimer) {
        clearTimeout(this.sseReconnectTimer);
        this.sseReconnectTimer = null;
      }
    },
  },

  created() {
    this.init();
    // 初始化SSE连接，进入系统时就建立连接
    this.initSSEConnection();
    // 先获取路由权限，再检查店铺配置
    this.getRouters().then(() => {
      // 查看店铺绑定数 手写版本
      list({

        source: "all",
        pageNum: 1,
        pageSize: 1000,
        orderByColumn: "createTime",
        isAsc: "desc",

      }).then(res => {
        if (res.data.code === 200) {
          const storeList = JSON.parse(JSON.stringify(res.data.rows || []));

          // 淘宝店铺
          const xiaoshouList = storeList.filter(
            (item) => item.source == "tb"
          );
          // 1688店铺
          const cangKuList = storeList.filter(
            (item) => item.source == "1688"
          );
          // 检查目标路由是否在权限路由中
          const hasPurchaseShopListRoute = this.checkRouteExists("/workbench/purchaseShopList");
          const hasTaobaoShopRoute = this.checkRouteExists("/workbench/TaobaoShop");

          // 没有淘宝店铺时，跳转到淘宝店铺配置页面（如果路由存在且当前不在该页面）
          if (xiaoshouList.length === 0 && hasPurchaseShopListRoute) {
            this.$router.push("/workbench/purchaseShopList");
          }
          // 没有1688店铺时，跳转到采购账号配置页面（如果路由存在且当前不在该页面）
          if (cangKuList.length === 0 && hasTaobaoShopRoute) {
            this.$router.push("/workbench/TaobaoShop");
          }

        }
      })


    })


  },
  destroyed() {
    // 组件销毁时关闭SSE连接
    this.closeSSEConnection();
  },
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 深色模式 - 全局背景 */
[data-theme="dark"] {
  background-color: #141b23;
}

[data-theme="dark"] body {
  background-color: #141b23;
  color: #e0e0e0;
}

[data-theme="dark"] #app {
  background-color: #141b23;
  color: #e0e0e0;
}

/* 覆盖 Element UI 主题色 - 使用CSS变量 */
.el-button--primary {
  background-color: var(--el-color-primary, #0f59a4) !important;
  border-color: var(--el-color-primary, #0f59a4) !important;
}

.el-button--primary:hover,
.el-button--primary:focus {
  background-color: var(--el-color-primary-dark-2, #0d4a8a) !important;
  border-color: var(--el-color-primary-dark-2, #0d4a8a) !important;
}

.el-button--primary:active {
  background-color: var(--el-color-primary-dark-2, #0a3b70) !important;
  border-color: var(--el-color-primary-dark-2, #0a3b70) !important;
}

#app {
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
  /* overflow: hidden; */
}

#workbench {
  height: 100vh;
  overflow: hidden;
}

#workbench .el-container {
  height: 100vh;
}

/*
 * 与 App.vue 中断点一致（max-width: 1920px）。
 * min-width 取 1600：在 1920 逻辑宽屏约 125% 缩放时视口约 1536px，仍大于 1280 故原先无横条；
 * 1600 > 1536 会触发横向滚动，避免侧栏+主区被挤变形。
 */
/* @media screen and (max-width: 1920px) {
  #app {
    overflow-x: auto;
  }

  #workbench {
    min-width: 1600px;
    height: 100vh;
    overflow-x: visible;
    overflow-y: hidden;
  }
} */

/* 顶部导航栏 - 与BlueWhaleWeb保持一致 */
.top-header.layout-navbars-breadcrumb-index {
  height: 50px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--next-bg-topBar, #fff);
  border-bottom: 1px solid var(--next-border-color-light, #e4e7ed);
  padding: 0;
}

.logo,
.sidebar-logo {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  color: #0f59a4;
  transition: width 0.3s ease;
}

.logo-icon {
  font-size: 20px;
  margin-right: 6px;
  transition: margin-right 0.3s ease;
}

.logo-text {
  font-size: 16px;
  transition: opacity 0.3s ease, width 0.3s ease, margin 0.3s ease;
  white-space: nowrap;
}

#workbench.is-collapsed .logo-text {
  opacity: 0;
  width: 0;
  margin: 0;
}

#workbench.is-collapsed .logo-icon {
  margin-right: 0;
}

/* 面包屑样式 - 与BlueWhaleWeb保持一致 */
.header-breadcrumb.layout-navbars-breadcrumb {
  flex: 1;
  height: inherit;
  display: flex;
  align-items: center;
}

/* 独立折叠按钮容器（当面包屑隐藏时） */
.collapse-btn-header-standalone {
  display: flex;
  align-items: center;
  height: inherit;
  flex-shrink: 0;
}

/* 折叠/展开按钮 - 在面包屑内部 */
.header-breadcrumb .collapse-btn-header.layout-navbars-breadcrumb-icon {
  cursor: pointer;
  font-size: 18px;
  color: var(--next-bg-topBarColor, #606266);
  height: 100%;
  width: 40px;
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s;
  flex-shrink: 0;
}

.header-breadcrumb .collapse-btn-header.layout-navbars-breadcrumb-icon:hover {
  opacity: 1;
}

.header-breadcrumb .layout-navbars-breadcrumb-iconfont {
  font-size: 14px;
  margin-right: 5px;
}

.header-breadcrumb .layout-navbars-breadcrumb-span {
  display: flex;
  align-items: center;
  opacity: 0.7;
  color: var(--next-bg-topBarColor, #606266);
}

.header-breadcrumb .breadcrumb-link {
  display: flex;
  align-items: center;
  /* border: 1px solid red; */
  /* widows: 100px; */
  color: var(--next-bg-topBarColor, #606266);
  text-decoration: none;
  transition: color 0.3s;
}

.header-breadcrumb .breadcrumb-link:hover {
  color: var(--el-color-primary, #0f59a4) !important;
}

.header-breadcrumb :deep(.el-breadcrumb__separator) {
  opacity: 0.7;
  color: var(--next-bg-topBarColor, #606266);
}

.header-breadcrumb :deep(.el-breadcrumb__inner a, .el-breadcrumb__inner.is-link) {
  font-weight: unset !important;
  color: var(--next-bg-topBarColor, #606266);
}

.header-breadcrumb :deep(.el-breadcrumb__inner a:hover) {
  color: var(--el-color-primary, #0f59a4) !important;
}

.header-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
}

.nav-buttons {
  display: flex;
  gap: 10px;
}

.nav-btn {
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  height: 36px;
}

.nav-btn:not(.el-button--primary) {
  background-color: #fff;
  border-color: #dcdfe6;
  color: #606266;
}

.nav-btn.el-button--primary,
.nav-btn-active {
  background-color: var(--el-color-primary, #0f59a4);
  border-color: var(--el-color-primary, #0f59a4);
  color: #fff;
}

.greeting {
  color: #67c23a;
  font-size: 14px;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  gap: 0;
  margin-left: auto;
  flex-shrink: 0;
}

.header-right>* {
  margin-left: 0;
}

.header-right i {
  font-size: 16px;
  color: var(--next-bg-topBarColor, #909399);
  cursor: pointer;
  transition: all 0.3s;
  padding: 0 10px;
  height: 50px;
  line-height: 50px;
  display: flex;
  align-items: center;
}

.header-right i:hover {
  background: var(--next-color-user-hover, rgba(0, 0, 0, 0.05));
  color: var(--el-color-primary, #0f59a4);
}

/* 图标容器样式 */
.header-icon-item {
  font-size: 16px;
  color: var(--next-bg-topBarColor, #909399);
  cursor: pointer;
  transition: all 0.3s;
  padding: 0 10px;
  height: 50px;
  line-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon-item:hover {
  background: var(--next-color-user-hover, rgba(0, 0, 0, 0.05));
  color: var(--el-color-primary, #0f59a4);
}

/* SVG 图标容器 */
.svg-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 50px;
  color: var(--next-bg-topBarColor, #909399);
  transition: all 0.3s;
}

.svg-icon-wrapper:hover {
  background: var(--next-color-user-hover, rgba(0, 0, 0, 0.05));
  color: var(--el-color-primary, #0f59a4);
}

/* SVG 图标样式 */
.header-svg-icon {
  width: 18px;
  height: 18px;
  fill: currentColor;
  transition: all 0.3s;
}

.svg-icon-wrapper:hover .header-svg-icon {
  transform: scale(1.1);
}

.layout-settings-icon {
  font-size: 18px;
}

.settings-icon {
  position: relative;
}

/* Tooltip 白色背景样式 */
.header-right :deep(.el-tooltip__popper.is-light) {
  background-color: #fff !important;
  border: 1px solid #e4e7ed;
  color: #606266;
}

.header-right :deep(.el-tooltip__popper.is-light .el-tooltip__arrow::before) {
  background-color: #fff !important;
  border: 1px solid #e4e7ed;
}

/* 下拉菜单样式 */
.el-dropdown-menu__item.is-active {
  color: #0f59a4;
  background-color: rgba(15, 89, 164, 0.1);
}

.el-dropdown-menu__item {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
}

.el-dropdown-menu__item i.el-icon-check {
  margin-left: auto;
  color: #0f59a4;
  font-weight: bold;
}

.user-dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  height: 50px;
}

.user-dropdown:hover {
  background-color: var(--next-color-user-hover, rgba(0, 0, 0, 0.05));
}

.user-dropdown i {
  color: #606266;
  font-size: 16px;
}

.user-dropdown .el-icon-arrow-down {
  font-size: 12px;
  margin-left: 4px;
  transition: transform 0.3s;
}

.user-dropdown:hover .el-icon-arrow-down {
  transform: rotate(180deg);
}

.username {
  color: #303133;
  font-size: 13px;
  margin-left: 5px;
  margin-right: 4px;
}


/* 左侧导航栏 */
.sidebar {
  background-color: var(--next-bg-menuBar, #fff);
  color: var(--next-bg-menuBarColor, #333);
  border-right: 1px solid #e4e7ed;
  height: 100vh;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
}

.el-menu {
  border-right: none !important;
}

/* Logo样式 - 在侧边栏顶部 */
.sidebar .layout-logo {
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: rgb(0 21 41 / 2%) 0px 1px 4px;
  color: var(--el-color-primary, #0f59a4);
  font-size: 16px;
  cursor: pointer;
  animation: logoAnimation 0.3s ease-in-out;
  flex-shrink: 0;
  padding: 0 10px;
  border-bottom: 1px solid #e4e7ed;
  background-color: var(--next-bg-menuBar, #fff);
}

.layout-logo .logo-icon {
  font-size: 20px;
  margin-right: 8px;
}

.layout-logo .logo-text {
  white-space: nowrap;
  display: inline-block;
  font-weight: 800;
  font-size: 18px;
}

.layout-logo:hover .logo-text {
  color: var(--color-primary-light-2, #3d7bc4);
}

.sidebar .layout-logo.logo-collapsed {
  width: 100%;
  padding: 0;
  justify-content: center;
}

.sidebar .layout-logo.logo-collapsed .logo-text {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.sidebar .layout-logo.logo-collapsed .logo-icon {
  margin-right: 0;
}

@keyframes logoAnimation {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}

.sidebar-menu {
  border: none !important;
  height: calc(100vh - 50px);
  overflow-y: auto;
  overflow-x: hidden;
  background-color: var(--next-bg-menuBar, #fff) !important;
  color: var(--next-bg-menuBarColor, #333) !important;
}

/* Element UI 菜单宽度 */

.sidebar-menu .el-menu-item {
  height: 50px;
  line-height: 50px;
}

.sidebar-menu .el-menu-item.is-active {
  background-color: var(--el-menu-active-bg-color,
      rgba(15, 89, 164, 0.1)) !important;
  color: var(--el-menu-active-text-color,
      var(--el-color-primary, #0f59a4)) !important;
}

.sidebar-menu .el-submenu__title {
  height: 50px;
  line-height: 50px;
}

/* 折叠状态下的样式调整 */
.sidebar-menu.el-menu--collapse .el-submenu__title,
.sidebar-menu.el-menu--collapse .el-menu-item {
  padding-left: 20px !important;
  text-align: center;
}

/* 主内容区 */
.main-content {
  background-color: #f5f7fa;
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.el-main {
  padding: 0px !important;
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* 动态导航标签容器 */
.nav-tabs-container {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  /* 统一保持一行，避免换行导致图标/文字溢出重叠 */
  overflow-x: auto;
  overflow-y: hidden;
  cursor: move;
  flex: 1;
  min-width: 0;
  align-items: flex-start;
  align-content: flex-start;
}

/* 深色模式下的标签容器 */
[data-theme="dark"] .nav-tabs-container {
  background-color: #1e2832;
  border-bottom-color: #3d4d5f;
}

.nav-tabs-container .nav-tab-item {
  cursor: pointer;
  user-select: none;
}

.main-router {
  background-color: #f5f7fa;
  padding: 10px !important;
  flex: 1;
  min-height: 0;
  position: relative;
  /* 改为使用页面滚动，便于 sticky 吸顶 */
  overflow: visible;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* 路由切换动画 - 只显示进入动画，离开时立即隐藏 */
.slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  position: relative;
  width: 100%;
  min-height: 100%;
}

.slide-leave-active {
  /* 离开时立即隐藏，不显示动画 */
  transition: opacity 0s;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
}

.slide-enter {
  transform: translateX(100%);
}

.slide-enter-to {
  transform: translateX(0);
}

.slide-leave {
  /* 离开时立即隐藏 */
  opacity: 0;
  visibility: hidden;
}

.slide-leave-to {
  /* 离开时立即隐藏 */
  opacity: 0;
  visibility: hidden;
}

/* 确保 router-view 正确显示 */
.main-router>* {
  width: 100%;
  min-height: 100%;
  display: block !important;
  visibility: visible !important;
}

/* 确保 transition 内的 router-view 正确显示 */
.main-router .slide-enter-active,
.main-router .slide-enter-to {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.nav-tab-item {
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 12px;
  height: 28px;
  line-height: 28px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  border: 1px solid #dcdfe6;
  background-color: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.1s;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  /* 防止标题/图标溢出导致相邻 tab 重叠 */
  max-width: 260px;
  min-width: 0;
  flex: 0 0 auto;
  /* 防止在 nowrap 容器中被挤压/错位 */
}

.nav-tab-item:hover {
  border-color: var(--el-color-primary, #0f59a4);
}

.nav-tab-item.nav-tab-active {
  background-color: var(--el-color-primary, #0f59a4);
  border-color: var(--el-color-primary, #0f59a4);
  color: #fff;
}

/* 深色模式下的标签样式 */
[data-theme="dark"] .nav-tab-item {
  background-color: #2c3a49;
  border-color: #3d4d5f;
  color: #f0f0f0;
}

[data-theme="dark"] .nav-tab-item:hover {
  border-color: var(--el-color-primary, #0f59a4);
  background-color: #34495e;
  color: #ffffff;
}

[data-theme="dark"] .nav-tab-item.nav-tab-active {
  background-color: var(--el-color-primary, #0f59a4);
  border-color: var(--el-color-primary, #0f59a4);
  color: #fff;
}

[data-theme="dark"] .nav-tab-item i {
  color: inherit;
}

[data-theme="dark"] .nav-tab-item.nav-tab-active i {
  color: #fff;
}

.nav-tab-item i {
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-tab-item span {
  line-height: 1;
  display: inline-block;
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.nav-tab-item .refresh-icon {
  margin-left: 4px;
  cursor: pointer;
  transition: transform 0.3s;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.nav-tab-item .refresh-icon:hover {
  transform: rotate(180deg);
}

.nav-tab-item .close-icon {
  margin-left: 4px;
  cursor: pointer;
  padding: 0;
  border-radius: 2px;
  transition: all 0.3s;
  font-size: 12px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.nav-tab-item .close-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.nav-tab-item.nav-tab-active .close-icon:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* Sortable.js 拖拽态样式，避免拖拽幽灵层“看起来像正常 tab” */
:global(.sortable-ghost),
:global(.sortable-fallback),
:global(.sortable-drag) {
  opacity: 0.85;
}

:global(.sortable-chosen) {
  opacity: 0.75;
}

/* 深色模式下关闭按钮的悬停效果 */
[data-theme="dark"] .nav-tab-item .close-icon:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

[data-theme="dark"] .nav-tab-item.nav-tab-active .close-icon:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.main-footer {
  color: #5555;
  text-align: center;
  line-height: 55px;
  margin-top: auto;
}

.layout-navbars-breadcrumb-hide {
  min-width: 50px;
  /* border: 1px saddlebrown solid; */
  display: flex;
  flex-direction: row;
  align-items: center;
}

/* ==================== 深色模式全局样式 ==================== */
[data-theme="dark"] {
  color-scheme: dark;
}

/* 深色模式 - 顶部导航栏 */
[data-theme="dark"] .top-header.layout-navbars-breadcrumb-index {
  background: #1e2832 !important;
  border-bottom-color: #3d4d5f !important;
}

[data-theme="dark"] .header-breadcrumb .layout-navbars-breadcrumb-span,
[data-theme="dark"] .header-breadcrumb .breadcrumb-link,
[data-theme="dark"] .header-breadcrumb :deep(.el-breadcrumb__inner),
[data-theme="dark"] .header-breadcrumb :deep(.el-breadcrumb__separator) {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .header-breadcrumb .breadcrumb-link:hover,
[data-theme="dark"] .header-breadcrumb :deep(.el-breadcrumb__inner a:hover) {
  color: var(--el-color-primary, #0f59a4) !important;
}

[data-theme="dark"] .header-breadcrumb .collapse-btn-header {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .header-right i,
[data-theme="dark"] .header-icon-item,
[data-theme="dark"] .svg-icon-wrapper {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .header-right i:hover,
[data-theme="dark"] .header-icon-item:hover,
[data-theme="dark"] .svg-icon-wrapper:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  color: var(--el-color-primary, #0f59a4) !important;
}

[data-theme="dark"] .user-dropdown {
  color: #e0e0e0;
}

[data-theme="dark"] .user-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

[data-theme="dark"] .user-dropdown i,
[data-theme="dark"] .username {
  color: #e0e0e0 !important;
}

/* 深色模式 - 侧边栏 */
[data-theme="dark"] .sidebar {
  background-color: #1a2329 !important;
  border-right-color: #3d4d5f !important;
}

[data-theme="dark"] .sidebar .layout-logo {
  background-color: #1a2329 !important;
  border-bottom-color: #3d4d5f !important;
  color: var(--el-color-primary, #0f59a4) !important;
}

[data-theme="dark"] .sidebar-menu {
  background-color: #1a2329 !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .sidebar-menu .el-menu-item,
[data-theme="dark"] .sidebar-menu .el-submenu__title {
  color: #d0d0d0 !important;
  background-color: transparent !important;
}

[data-theme="dark"] .sidebar-menu .el-menu-item:hover,
[data-theme="dark"] .sidebar-menu .el-submenu__title:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}

[data-theme="dark"] .sidebar-menu .el-menu-item.is-active {
  background-color: var(--el-menu-active-bg-color,
      rgba(15, 89, 164, 0.25)) !important;
  color: var(--el-menu-active-text-color,
      var(--el-color-primary, #0f59a4)) !important;
}

[data-theme="dark"] .sidebar-menu .el-submenu__title.is-active {
  color: var(--el-menu-active-text-color,
      var(--el-color-primary, #0f59a4)) !important;
}

[data-theme="dark"] .sidebar-menu .el-submenu .el-menu {
  background-color: #151b20 !important;
}

[data-theme="dark"] .sidebar-menu .el-submenu .el-menu-item {
  background-color: #151b20 !important;
}

[data-theme="dark"] .sidebar-menu .el-submenu .el-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* 深色模式 - 主内容区 */
[data-theme="dark"] .main-content {
  background-color: #141b23 !important;
}

[data-theme="dark"] .main-router {
  background-color: #141b23 !important;
}

[data-theme="dark"] .main-footer {
  color: #999 !important;
}

/* 深色模式 - 按钮 */
[data-theme="dark"] .nav-btn:not(.el-button--primary) {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .nav-btn:not(.el-button--primary):hover {
  background-color: #34495e !important;
  border-color: var(--el-color-primary, #0f59a4) !important;
  color: #ffffff !important;
}

/* 深色模式 - 下拉菜单 */
[data-theme="dark"] .el-dropdown-menu {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
}

[data-theme="dark"] .el-dropdown-menu__item {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-dropdown-menu__item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
}

[data-theme="dark"] .el-dropdown-menu__item.is-active {
  color: var(--el-color-primary, #0f59a4) !important;
  background-color: rgba(15, 89, 164, 0.2) !important;
}

/* 深色模式 - Tooltip */
[data-theme="dark"] .header-right :deep(.el-tooltip__popper.is-light) {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .header-right :deep(.el-tooltip__popper.is-light .el-tooltip__arrow::before) {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
}

/* 深色模式 - Element UI 组件通用样式 */
[data-theme="dark"] .el-card {
  background-color: #1e2832 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

/* 默认表格样式（非深色模式） */
.el-table {
  background-color: #ffffff !important;
  color: #606266 !important;
}

.el-table__body-wrapper {
  background-color: #ffffff !important;
}

.el-table__body tr {
  background-color: #ffffff !important;
}

.el-table__body td {
  background-color: #ffffff !important;
  border-color: #ebeef5 !important;
  color: #606266 !important;
}

.el-table th.el-table__cell {
  background-color: #f8f8f8 !important;
  color: #606266 !important;
  border-color: #ebeef5 !important;
}

.el-table thead {
  background-color: #f8f8f8 !important;
}

.el-table thead th {
  background-color: #f8f8f8 !important;
  color: #606266 !important;
  border-color: #ebeef5 !important;
}

.el-table td.el-table__cell {
  background-color: #ffffff !important;
  border-color: #ebeef5 !important;
  color: #606266 !important;
}

.el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #fafafa !important;
}

.el-table--enable-row-hover .el-table__body tr:hover>td {
  background-color: #f5f7fa !important;
}

.el-table__row {
  background-color: #ffffff !important;
}

.el-table__row:hover {
  background-color: #f5f7fa !important;
}

.el-table__row:hover td {
  background-color: #f5f7fa !important;
}

/* 深色模式 - 表格样式优化 */
[data-theme="dark"] .el-table {
  background-color: #1e2832 !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-table__body-wrapper {
  background-color: #1e2832 !important;
}

[data-theme="dark"] .el-table__body tr {
  background-color: #1e2832 !important;
}

[data-theme="dark"] .el-table__body td {
  background-color: #1e2832 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-table th.el-table__cell {
  background-color: #252f3d !important;
  color: #e0e0e0 !important;
  border-color: #3d4d5f !important;
}

[data-theme="dark"] .el-table thead {
  background-color: #252f3d !important;
}

[data-theme="dark"] .el-table thead th {
  background-color: #252f3d !important;
  color: #e0e0e0 !important;
  border-color: #3d4d5f !important;
}

[data-theme="dark"] .el-table td.el-table__cell {
  background-color: #1e2832 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #252f3d !important;
}

[data-theme="dark"] .el-table--enable-row-hover .el-table__body tr:hover>td {
  background-color: #2a3542 !important;
}

[data-theme="dark"] .el-table__row {
  background-color: #1e2832 !important;
}

[data-theme="dark"] .el-table__row:hover {
  background-color: #2a3542 !important;
}

[data-theme="dark"] .el-table__row:hover td {
  background-color: #2a3542 !important;
}

[data-theme="dark"] .el-input__inner {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-input__inner::placeholder {
  color: #999 !important;
}

[data-theme="dark"] .el-textarea__inner {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-select-dropdown {
  background-color: #2c3a49 !important;
  border-color: #3d4d5f !important;
}

[data-theme="dark"] .el-select-dropdown__item {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-select-dropdown__item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

[data-theme="dark"] .el-dialog {
  background-color: #1e2832 !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-dialog__header {
  border-bottom-color: #3d4d5f !important;
}

[data-theme="dark"] .el-dialog__title {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-drawer {
  background-color: #1e2832 !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-drawer__header {
  border-bottom-color: #3d4d5f !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-pagination {
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-pagination .el-pager li {
  background-color: #2c3a49 !important;
  color: #e0e0e0 !important;
}

[data-theme="dark"] .el-pagination .el-pager li:hover {
  color: var(--el-color-primary, #0f59a4) !important;
}

[data-theme="dark"] .el-pagination .el-pager li.is-active {
  background-color: var(--el-color-primary, #0f59a4) !important;
  color: #ffffff !important;
}

/* ==================== 布局切换样式 ==================== */

/* transverse 布局样式 */
.layout-transverse .transverse-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  height: 50px !important;
}

.transverse-menu-container {
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
  overflow: hidden;
}

.transverse-logo {
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 100%;
  color: var(--el-color-primary, #0f59a4);
  font-size: 16px;
  font-weight: bold;
  border-right: 1px solid var(--next-border-color-light, #e4e7ed);
  flex-shrink: 0;
  cursor: pointer;
}

.transverse-logo .logo-icon {
  font-size: 20px;
  margin-right: 8px;
}

.transverse-logo .logo-text {
  white-space: nowrap;
  font-weight: 800;
  font-size: 18px;
}

.transverse-menu {
  flex: 1;
  height: 100%;
  border-bottom: none !important;
  display: flex;
  align-items: center;
}

.transverse-menu .el-menu-item {
  height: 50px;
  line-height: 50px;
  padding: 0 20px;
  border-bottom: 2px solid transparent;
  transition: all 0.3s;
}

.transverse-menu .el-menu-item.is-active {
  border-bottom-color: var(--el-color-primary, #0f59a4);
  background-color: transparent !important;
}

.transverse-menu .el-menu-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.transverse-menu .el-menu-item i {
  margin-right: 5px;
}

[data-theme="dark"] .transverse-logo {
  border-right-color: #3d4d5f !important;
  color: var(--el-color-primary, #0f59a4) !important;
}

[data-theme="dark"] .transverse-menu .el-menu-item,
[data-theme="dark"] .transverse-menu .el-submenu__title {
  color: #d0d0d0 !important;
}

[data-theme="dark"] .transverse-menu .el-menu-item:hover,
[data-theme="dark"] .transverse-menu .el-submenu__title:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}

[data-theme="dark"] .transverse-menu .el-menu-item.is-active {
  background-color: var(--el-menu-active-bg-color,
      rgba(15, 89, 164, 0.25)) !important;
  color: var(--el-menu-active-text-color,
      var(--el-color-primary, #0f59a4)) !important;
}

.waiBu {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #ffffff;
  border-bottom: 1px solid #e4e7ed;
  gap: 10px;
}

.waiLeft {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: auto;
}

/* 深色模式下的 waiBu */
[data-theme="dark"] .waiBu {
  background-color: #1e2832;
  border-bottom-color: #3d4d5f;
}

/* 客服悬浮按钮样式 */
.customer-service-float {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 9999;
  /* background-color: var(--el-color-primary, #0f59a4) !important; */

  cursor: move;
}

.service-button {
  width: 60px;
  background-color: var(--el-color-primary, #0f59a4) !important;
  height: 60px;
  border-radius: 50%;
  /* background-color: #1890ff; */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.service-button:hover {
  background-color: #40a9ff;
  transform: scale(1.05);
}

.service-button.hovered {
  background-color: #40a9ff;
}

.service-panel {
  position: absolute;
  right: 70px;
  bottom: 0;
  width: 200px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.service-header {
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.service-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.service-content {
  font-size: 14px;
  color: #666;
}

.service-time {
  margin: 0 0 12px 0;
}

.service-qrcode {
  text-align: center;
  margin: 12px 0;
}

.service-qrcode img {
  width: 120px;
  height: 120px;
}

.service-contact {
  margin: 12px 0 4px 0;
}

.service-wechat {
  margin: 4px 0 0 0;
}

/* 深色模式下的客服悬浮按钮 */
[data-theme="dark"] .service-panel {
  background-color: #1e2832;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .service-header {
  border-bottom-color: #3d4d5f;
}

[data-theme="dark"] .service-header h3 {
  color: #d0d0d0;
}

[data-theme="dark"] .service-content {
  color: #b0b0b0;
}

/* 消息通知抽屉样式 */
.notification-container {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

.empty-notification {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.empty-notification p {
  margin-top: 16px;
  font-size: 14px;
}

.notification-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notification-item {
  padding: 12px;
  background-color: #fafafa;
  border-radius: 8px;
  border-left: 4px solid #0f59a4;
  transition: all 0.3s;
}

.notification-item:hover {
  background-color: #f5f5f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.notification-level {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.notification-level.info {
  background-color: #e8f4fd;
  color: #1890ff;
}

.notification-level.warning {
  background-color: #fff7e6;
  color: #fa8c16;
}

.notification-level.error {
  background-color: #fff1f0;
  color: #f5222d;
}

.notification-level.success {
  background-color: #f6ffed;
  color: #52c41a;
}

.notification-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.notification-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #909399;
}

.notification-shop {
  background-color: #e4e7ed;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 深色模式下的消息通知 */
[data-theme="dark"] .notification-item {
  background-color: #252f3d;
  border-left-color: #1890ff;
}

[data-theme="dark"] .notification-item:hover {
  background-color: #2a3542;
}

[data-theme="dark"] .notification-title {
  color: #e0e0e0;
}

[data-theme="dark"] .notification-content {
  color: #b0b0b0;
}

[data-theme="dark"] .notification-footer {
  color: #808080;
}

[data-theme="dark"] .notification-shop {
  background-color: #3d4d5f;
}

/* Badge通知徽章样式 */
.badge-notification {
  position: relative;
  display: inline-block;
  padding-right: 8px;
}

.badge-notification .el-badge__content {
  position: absolute;
  top: -4px;
  right: -2px;
  font-size: 10px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
  transform: translateX(50%);
}

/* 深色模式下的Badge */
[data-theme="dark"] .badge-notification .el-badge__content {
  background-color: #f5222d !important;
}
</style>
